import React from 'react'
import { makeStyles, Tooltip } from '@material-ui/core'
import PropTypes from 'prop-types'
import OperationalTable from '../../../../organisms/OperationalTable'
import { useDeleteClientTagOption } from '../../../../../api/dataMaintenance'
import SydButton from '../../../../commonDesign/Button'
import Loading from '../../../../molecules/Loading'
import ErrorDisplay from '../../../../molecules/ErrorDisplay'
import ConfirmationModal from '../../../../molecules/ConfirmationModal'

export const TagOptionsTab = ({ memberDetailsData, memberLoading, setSelectedGroup, setModalOpen, isClientSpecific }) => {
  const { mutateAsync, isLoading: deleteLoading } = useDeleteClientTagOption()
  const [deletedId, setDeletedId] = React.useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  const defaultColumnConfig = {
    columns: [
      { Header: 'Id', accessor: 'groupId', type: 'number' },
      { Header: 'Tag Option Name', accessor: 'longName', type: 'string' },
      { Header: 'Member Count', accessor: 'memberCount.memberCount' ?? 0, type: 'number', width: 20 },
      {
        Header: 'Color',
        accessor: 'colorGroup',
        Cell: ({ row }) => (ColorField({ hex: row.original.colorField })),
        width: 90
      },
      { Header: 'Ordinal', accessor: 'ordinal', type: 'string' },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => ActionField({
          group: row.original,
          editFunction: () => {
            setSelectedGroup(row.original)
            setModalOpen(true)
          },
          deleteFunction: () => {
            setDeletedId(row.original.groupId)
            setDeleteModalOpen(true)
          },
          isLoading: deleteLoading && deletedId === row.original.groupId,
          canDelete: row.original.memberCount.memberCount <= 0,
          noDeleteReason: 'Group with members cannot be deleted'
        })
      }
    ],
    defaultSort: [{ id: 'groupId', desc: false }]
  }
  if (isClientSpecific) return <ErrorDisplay error='These Tag options are managed with Admin > Clients' soft />
  if (memberLoading) return <Loading />
  return (
    <div>
      <ConfirmationModal
        open={deleteModalOpen || deleteLoading}
        isLoading={deleteLoading}
        onCancel={() => {
          setDeletedId(null)
          setDeleteModalOpen(false)
        }}
        buttonSize='medium'
        confirmationText='Are you sure you want to delete this tag?'
        buttonLabelConfirm='Delete'
        buttonLabelCancel='Cancel'
        confirmationType='delete'
        onConfirm={async () => {
          const result = await mutateAsync({ groupId: deletedId })
          if (result.success) {
            setDeletedId(null)
            setDeleteModalOpen(false)
          } else {
            console.error(result)
          }
        }}
      />
      <OperationalTable.Wrapper>
        <OperationalTable
          variant='v2'
          mode='auto'
          itemName='Groups'
          data={memberDetailsData || []}
          loading={memberLoading}
          total={memberDetailsData?.length}
          columns={defaultColumnConfig.columns}
          defaultSort={defaultColumnConfig.defaultSort}
        />
      </OperationalTable.Wrapper>
    </div>
  )
}

TagOptionsTab.propTypes = {
  memberDetailsData: PropTypes.array,
  memberLoading: PropTypes.bool,
  setSelectedGroup: PropTypes.func,
  setModalOpen: PropTypes.func,
  isClientSpecific: PropTypes.bool
}

export const ActionField = ({ canDelete = true, noDeleteReason = '', deleteFunction, editFunction }) => {
  return (
    <div className='flex flex-row items-center'>
      <div className='flex flex-row gap-2 pr-4'>
        <SydButton size='sm' variant='outline' icon='edit' onClick={() => editFunction()}>Edit</SydButton>
        {!canDelete ? (
          <Tooltip title={noDeleteReason} placement='top-start'>
            <div>
              <SydButton size='sm' variant='outline' disabled icon='delete'>Delete</SydButton>
            </div>
          </Tooltip>
        ) : (
          <SydButton size='sm' variant='outline' priority='warning' icon='delete' onClick={deleteFunction}>Delete</SydButton>
        )}
      </div>
    </div>
  )
}

ActionField.propTypes = {
  canDelete: PropTypes.bool,
  noDeleteReason: PropTypes.string,
  editFunction: PropTypes.func,
  deleteFunction: PropTypes.func
}

const colorStyles = makeStyles((theme) => ({
  colorContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem'
  },
  colorLabel: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '125px',
    height: '20px'
  },
  swatch: (props) => ({
    width: '20px',
    height: '20px',
    backgroundColor: props.hex
  })
}))

export const ColorField = ({ hex }) => {
  const classes = colorStyles({ hex })
  return (
    <div className={classes.colorContainer}>
      <div className={classes.colorLabel}>
        <span>{hex}</span>
      </div>
      <div className={classes.swatch} />
    </div>
  )
}

ColorField.propTypes = {
  hex: PropTypes.string
}
